import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Copy, Download, Calendar, ArrowLeft, Building, Target, TrendingUp, AlertTriangle, Zap, DollarSign, Users, Shield, Map, BarChart } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { FaCircleArrowLeft } from 'react-icons/fa6';
import { useAuthCheck } from '../hooks/useAuthCheck';

function CategoryPage() {
    useAuthCheck();

    const { categoryKey } = useParams();
    const navigate = useNavigate();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [businessData, setBusinessData] = useState(null);
    const [currentSection, setCurrentSection] = useState(null);

    // Complete analysis sections mapping
    const analysisSections = [
        {
            title: "Current Performance",
            icon: <BarChart size={20} />,
            key: "currentPerformance",
            color: "blue"
        },
        {
            title: "SWOT Analysis",
            icon: <Target size={20} />,
            key: "swotAnalysis",
            color: "green"
        },
        {
            title: "Growth Opportunities",
            icon: <TrendingUp size={20} />,
            key: "growthOpportunities",
            color: "yellow"
        },
        {
            title: "Risk Assessment",
            icon: <AlertTriangle size={20} />,
            key: "riskAssessment",
            color: "red"
        },
        {
            title: "Operational Efficiency",
            icon: <Zap size={20} />,
            key: "operationalEfficiency",
            color: "emerald"
        },
        {
            title: "Financial Health",
            icon: <DollarSign size={20} />,
            key: "financialHealth",
            color: "purple"
        },
        {
            title: "Marketing Strategy",
            icon: <Target size={20} />,
            key: "marketingStrategy",
            color: "indigo"
        },
        {
            title: "Competitive Analysis",
            icon: <Shield size={20} />,
            key: "competitiveAnalysis",
            color: "orange"
        },
        {
            title: "Customer Analysis",
            icon: <Users size={20} />,
            key: "customerAnalysis",
            color: "teal"
        },
        {
            title: "Technology Assessment",
            icon: <Zap size={20} />,
            key: "technologyAssessment",
            color: "cyan"
        },
        {
            title: "Team Optimization",
            icon: <Users size={20} />,
            key: "teamOptimization",
            color: "pink"
        },
        {
            title: "Expansion Strategy",
            icon: <Map size={20} />,
            key: "expansionStrategy",
            color: "gray"
        }
    ];

    // Complete color classes function
    const getColorClasses = (color) => {
        const colors = {
            blue: {
                border: 'border-blue-200',
                bg: 'bg-blue-50',
                hover: 'hover:bg-blue-100',
                text: 'text-blue-700',
                button: 'btn-outline-primary',
                cardbg: '#dbeafe',
                cardborder: '#93c5fd',
                cardtext: '#1e40af'
            },
            green: {
                border: 'border-green-200',
                bg: 'bg-green-50',
                hover: 'hover:bg-green-100',
                text: 'text-green-700',
                button: 'btn-outline-success',
                cardbg: '#dcfce7',
                cardborder: '#86efac',
                cardtext: '#166534'
            },
            yellow: {
                border: 'border-yellow-200',
                bg: 'bg-yellow-50',
                hover: 'hover:bg-yellow-100',
                text: 'text-yellow-700',
                button: 'btn-outline-warning',
                cardbg: '#fef9c3',
                cardborder: '#fde047',
                cardtext: '#854d0e'
            },
            emerald: {
                border: 'border-emerald-200',
                bg: 'bg-emerald-50',
                hover: 'hover:bg-emerald-100',
                text: 'text-emerald-700',
                button: 'btn-outline-success',
                cardbg: '#d1fae5',
                cardborder: '#6ee7b7',
                cardtext: '#065f46'
            },
            red: {
                border: 'border-red-200',
                bg: 'bg-red-50',
                hover: 'hover:bg-red-100',
                text: 'text-red-700',
                button: 'btn-outline-danger',
                cardbg: '#fee2e2',
                cardborder: '#fca5a5',
                cardtext: '#991b1b'
            },
            purple: {
                border: 'border-purple-200',
                bg: 'bg-purple-50',
                hover: 'hover:bg-purple-100',
                text: 'text-purple-700',
                button: 'btn-outline-primary',
                cardbg: '#f3e8ff',
                cardborder: '#c4b5fd',
                cardtext: '#6b21a8'
            },
            indigo: {
                border: 'border-indigo-200',
                bg: 'bg-indigo-50',
                hover: 'hover:bg-indigo-100',
                text: 'text-indigo-700',
                button: 'btn-outline-primary',
                cardbg: '#e0e7ff',
                cardborder: '#a5b4fc',
                cardtext: '#3730a3'
            },
            orange: {
                border: 'border-orange-200',
                bg: 'bg-orange-50',
                hover: 'hover:bg-orange-100',
                text: 'text-orange-700',
                button: 'btn-outline-warning',
                cardbg: '#ffedd5',
                cardborder: '#fdba74',
                cardtext: '#9a3412'
            },
            teal: {
                border: 'border-teal-200',
                bg: 'bg-teal-50',
                hover: 'hover:bg-teal-100',
                text: 'text-teal-700',
                button: 'btn-outline-info',
                cardbg: '#ccfbf1',
                cardborder: '#5eead4',
                cardtext: '#134e4a'
            },
            pink: {
                border: 'border-pink-200',
                bg: 'bg-pink-50',
                hover: 'hover:bg-pink-100',
                text: 'text-pink-700',
                button: 'btn-outline-danger',
                cardbg: '#fce7f3',
                cardborder: '#f9a8d4',
                cardtext: '#831843'
            },
            cyan: {
                border: 'border-cyan-200',
                bg: 'bg-cyan-50',
                hover: 'hover:bg-cyan-100',
                text: 'text-cyan-700',
                button: 'btn-outline-info',
                cardbg: '#cffafe',
                cardborder: '#5eead4',
                cardtext: '#164e63'
            },
            gray: {
                border: 'border-gray-200',
                bg: 'bg-gray-50',
                hover: 'hover:bg-gray-100',
                text: 'text-gray-700',
                button: 'btn-outline-secondary',
                cardbg: '#f3f4f6',
                cardborder: '#d1d5db',
                cardtext: '#374151'
            }
        };
        return colors[color] || colors.gray;
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);

            try {
                // Get current section
                const section = analysisSections.find(s => s.key === categoryKey);
                setCurrentSection(section);

                if (!section) {
                    console.error('Section not found for key:', categoryKey);
                    navigate('/existing');
                    return;
                }

                // Get analysis data
                const savedAnalysis = localStorage.getItem('businessAnalysis');
                const savedFormData = localStorage.getItem('businessFormData');

                if (!savedAnalysis || !savedFormData) {
                    console.warn('No data found in localStorage');
                    navigate('/existing');
                    return;
                }

                const parsedAnalysis = JSON.parse(savedAnalysis);
                const parsedFormData = JSON.parse(savedFormData);

                // Check if we have data for this specific section
                if (!parsedAnalysis[categoryKey]) {
                    console.warn(`No data found for section: ${categoryKey}`);
                    // You can show a message or fetch from API
                }

                setAnalysis(parsedAnalysis);
                setBusinessData(parsedFormData);

            } catch (error) {
                console.error('Error loading data:', error);
                // Try to get from URL parameters if localStorage fails
                const urlParams = new URLSearchParams(window.location.search);
                const analysisParam = urlParams.get('analysis');
                const formParam = urlParams.get('form');

                if (analysisParam && formParam) {
                    try {
                        setAnalysis(JSON.parse(decodeURIComponent(analysisParam)));
                        setBusinessData(JSON.parse(decodeURIComponent(formParam)));
                    } catch (e) {
                        console.error('Error parsing URL params:', e);
                        navigate('/existing');
                    }
                } else {
                    navigate('/existing');
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [categoryKey]);

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const downloadAsPDF = async (sectionTitle, content) => {
        try {
            // First, get the formatted content as HTML
            let htmlContent;

            // If content is already formatted text
            if (typeof content === 'string') {
                // Format the content properly for PDF
                htmlContent = formatTextForPDF(content);
            } else {
                // For React elements, use the formattedContent from state
                htmlContent = extractContentAsHTML(formattedContent || content);
            }

            // Create a container element for html2pdf
            const element = document.createElement('div');

            // Create the HTML structure for PDF
            element.innerHTML = `
            <div style="padding: 40px; max-width: 800px; margin: 0 auto;">
                <!-- Header -->
                <div style="text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #0d53bd;">
                    <h1 style="color: #0d53bd; font-size: 28px; margin-bottom: 10px; font-weight: 700;">
                        ${sectionTitle}
                    </h1>
                    <div style="display: flex; justify-content: center; gap: 30px; margin-top: 15px; color: #666; font-size: 14px;">
                        <div>
                            <strong>Business:</strong> ${businessData?.businessName || 'Your Business'}
                        </div>
                        <div>
                            <strong>Date:</strong> ${new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })}
                        </div>
                    </div>
                </div>
                
                <!-- Content -->
                <div style="font-size: 14px; line-height: 1.7; color: #444;">
                    ${htmlContent}
                </div>
                
                <!-- Footer -->
                <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px;">
                    <p>Generated by InsightDraft AI • Confidential Business Analysis</p>
                    <p style="margin-top: 5px;">This document is for informational purposes only.</p>
                </div>
            </div>
        `;

            // Configure html2pdf options
            const options = {
                margin: 10,
                filename: `${sectionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    letterRendering: true,
                    windowWidth: 800
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4',
                    orientation: 'portrait',
                    compress: true
                },
                pagebreak: {
                    mode: ['avoid-all', 'css', 'legacy']
                }
            };

            // Generate and save PDF
            await html2pdf().set(options).from(element).save();

        } catch (error) {
            console.error('Error generating PDF:', error);

            // Fallback to HTML download if PDF generation fails
            alert('PDF generation failed. Downloading as HTML instead.');

            // Call the original HTML download function
            downloadAsHTML(sectionTitle, content);
        }
    };

    // Separate function for HTML download (as fallback)
    const downloadAsHTML = (sectionTitle, content) => {
        try {
            let htmlContent;

            if (typeof content === 'string') {
                htmlContent = formatTextForPDF(content);
            } else {
                htmlContent = extractContentAsHTML(formattedContent || content);
            }

            const fullHTML = `
<!DOCTYPE html>
<html>
<head>
    <title>${sectionTitle}</title>
    <meta charset="UTF-8">
    <style>
        body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
        h1 { color: #0d53bd; border-bottom: 2px solid #0d53bd; padding-bottom: 10px; }
        .content { margin-top: 30px; }
        .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; }
    </style>
</head>
<body>
    <h1>${sectionTitle}</h1>
    <div><strong>Business:</strong> ${businessData?.businessName || 'Your Business'}</div>
    <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
    
    <div class="content">
        ${htmlContent}
    </div>
    
    <div class="footer">
        Generated by InsightDraft AI • Confidential Business Analysis
    </div>
</body>
</html>`;

            const blob = new Blob([fullHTML], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sectionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading HTML:', error);

            // Final fallback: plain text
            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sectionTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    };

    // Helper function to extract HTML from React elements
    const extractContentAsHTML = (content) => {
        if (!content) return '';

        // If content is already a string
        if (typeof content === 'string') {
            return content;
        }

        // If content is React element with dangerouslySetInnerHTML
        if (content.props && content.props.dangerouslySetInnerHTML) {
            return content.props.dangerouslySetInnerHTML.__html || '';
        }

        // If content is React element with children
        if (content.props && content.props.children) {
            return String(content.props.children);
        }

        return String(content);
    };

    // Update formatTextForPDF to handle your specific formatting
    // Update formatTextForPDF to handle your specific formatting
    const formatTextForPDF = (text) => {
        if (!text) return '';

        const lines = text.split('\n');
        let formattedHTML = '';

        let inTable = false;
        let tableRows = [];

        lines.forEach((line, index) => {
            const trimmedLine = line.trim();

            // Check if this is a table
            if (trimmedLine.includes('|') && (trimmedLine.includes('---') || lines[index + 1]?.includes('---'))) {
                inTable = true;
                return;
            }

            if (inTable) {
                if (trimmedLine.includes('|')) {
                    tableRows.push(trimmedLine);
                } else {
                    // End of table, render it
                    formattedHTML += renderTableHTML(tableRows);
                    inTable = false;
                    tableRows = [];
                }
                return;
            }

            // Skip empty lines
            if (!trimmedLine) {
                formattedHTML += '<div style="height: 8px;"></div>';
                return;
            }

            // Handle headings (lines ending with colon)
            if (trimmedLine.endsWith(':') && trimmedLine.length < 100) {
                // Remove any * or ** from heading
                const cleanHeading = trimmedLine.replace(/\*\*/g, '').replace(/\*/g, '');
                formattedHTML += `<h3 style="color: #0d53bd; margin: 25px 0 15px 0; font-size: 18px; font-weight: 700;">${cleanHeading}</h3>`;
                return;
            }

            // Handle numbered items (1., 2., 3.)
            const numberedMatch = trimmedLine.match(/^(\d+\.)\s+(.+)/);
            if (numberedMatch) {
                const [, number, content] = numberedMatch;
                // Remove * and ** from content
                const cleanContent = content.replace(/\*\*/g, '').replace(/\*/g, '');
                formattedHTML += `
            <div style="margin: 15px 0; padding-left: 20px; display: flex; align-items: flex-start;">
                <span style="background: #0d53bd; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; margin-right: 10px; font-size: 12px; font-weight: bold;">
                    ${number.replace('.', '')}
                </span>
                <span>${cleanContent}</span>
            </div>
        `;
                return;
            }

            // Handle bullet points - first clean the line
            const cleanedLine = trimmedLine.replace(/\*\*/g, '').replace(/\*/g, '');

            // Check for bullet points after cleaning
            const bulletMatch = cleanedLine.match(/^[•*\-]\s+(.+)/);
            if (bulletMatch) {
                const content = bulletMatch[1];
                formattedHTML += `
            <div style="margin: 8px 0; padding-left: 20px; display: flex; align-items: flex-start;">
                <span style="color: #0d53bd; margin-right: 10px; font-weight: bold;">•</span>
                <span>${content}</span>
            </div>
        `;
                return;
            }

            // Handle text with colon in middle
            if (cleanedLine.includes(':') && !cleanedLine.endsWith(':')) {
                const colonIndex = cleanedLine.indexOf(':');
                const beforeColon = cleanedLine.substring(0, colonIndex + 1);
                const afterColon = cleanedLine.substring(colonIndex + 1).trim();

                formattedHTML += `
            <div style="margin: 15px 0;">
                <strong style="color: #0d53bd;">${beforeColon}</strong>
                <div style="margin-left: 20px; margin-top: 5px;">
                    ${afterColon}
                </div>
            </div>
        `;
                return;
            }

            // Regular paragraph - use cleaned line
            formattedHTML += `<p style="margin: 12px 0; line-height: 1.7;">${cleanedLine}</p>`;
        });

        // If still in table at the end
        if (inTable && tableRows.length > 0) {
            formattedHTML += renderTableHTML(tableRows);
        }

        return formattedHTML;
    };

    // Helper to render table HTML - also clean table cells
    const renderTableHTML = (rows) => {
        if (rows.length === 0) return '';

        let tableHTML = '<table style="width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 13px; border: 1px solid #e5e7eb;">';

        rows.forEach((row, rowIndex) => {
            // Clean the row before processing
            const cleanedRow = row.replace(/\*\*/g, '').replace(/\*/g, '');
            const cells = cleanedRow.split('|').filter(cell => cell.trim() !== '');

            // Skip separator rows
            if (cells.some(cell => cell.includes('---'))) {
                return;
            }

            const tag = rowIndex === 0 ? 'th' : 'td';
            const style = rowIndex === 0 ?
                'background: #f3f4f6; font-weight: 600; color: #374151; border: 1px solid #d1d5db;' :
                'border: 1px solid #e5e7eb;';

            tableHTML += `<tr style="${rowIndex % 2 === 1 ? 'background: #f9fafb;' : ''}">`;

            cells.forEach((cell, cellIndex) => {
                const cellStyle = `${style} padding: 12px 15px; text-align: left;`;
                tableHTML += `<${tag} style="${cellStyle}">${cell.trim()}</${tag}>`;
            });

            tableHTML += '</tr>';
        });

        tableHTML += '</table>';
        return tableHTML;
    };

    const renderFormattedContent = (text) => {
        if (!text) return null;

        const colorClass = currentSection ? getColorClasses(currentSection.color) : getColorClasses('gray');

        const formatText = (content) => {
            // Format markdown bold syntax
            content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

            // Format markdown italic syntax
            content = content.replace(/\*(?!\s)(.*?)\*/g, '<em>$1</em>');

            // Format ### as bold
            content = content.replace(/###(.*?)(?=###|$)/g, '<strong>$1</strong>');
            content = content.replace(/^###(.*)$/gm, '<strong>$1</strong>');

            content = content.replace(/^##(.*)$/gm, `<strong class="bold-heading" style="color: ${colorClass.cardtext}; font-size: 20px;">$1</strong>`);

            content = content.replace(/^###\s+(.*)$/gm, '<strong>$1</strong>');

            // जो text last में * से end हो, उसे bold करें
            content = content.replace(/(.*)\*$/g, '<strong>$1</strong>');

            // Format numbered lists with bold titles
            content = content.replace(/(\d+\.\s+\*\*.*?\*\*)/g, (match) => {
                const parts = match.split(/(\d+\.\s+)/);
                if (parts.length >= 3) {
                    const number = parts[1];
                    const boldText = parts[2].replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                    return `<div class="numbered-item"><span class="number">${number}</span>${boldText}</div>`;
                }
                return match;
            });

            content = content.replace(/^([A-Z][A-Z\s\*\-_:;0-9]{10,})$/gm, (match) => {
                const words = match.trim().split(/\s+/);
                if (words.length >= 2 && match === match.toUpperCase()) {
                    return `<strong style="color: ${colorClass.cardtext}; font-size: 20px;">${match}</strong>`;
                }
                return match;
            });

            content = content.replace(/^(Social Media|Email Marketing|Primary persona|Secondary persona|Target Audience|Channel Optimization|Strengths|Weaknesses|Opportunities|Threats|Market Risks|Operational Risks|Financial Risks|Competitive Risks|Regulatory Risks|Feasibility|Priority|Risk Assessment|Risk Matrix|Probability|Impact|Risk Score|Mitigation Strategies|Recommendations):$/gmi, '<strong>$1:</strong>');

            return content;
        };

        const detectContentType = (content) => {
            const trimmed = content.trim();

            if (content.includes('|') && content.includes('---') && content.includes('Risk')) {
                return 'riskTable';
            }

            if (content.includes('1. **') || content.includes('\n1.')) {
                return 'numberedList';
            }

            // Check for bullet points that are actually subheadings
            const isBulletSubheading = (trimmed.startsWith('•') || trimmed.startsWith('*') || trimmed.startsWith('-')) &&
                trimmed.includes(':') &&
                trimmed.length < 60 &&
                trimmed.split(' ').length <= 5;

            if (isBulletSubheading) {
                return 'boldTitle';  // Treat as bold title instead of bullet list
            }

            if (content.includes('* ') && content.includes(':')) {
                return 'titleWithPoints';
            }

            if (content.includes('* ') || content.includes('- ')) {
                return 'bulletList';
            }

            if (content.includes('**') && content.length < 100) {
                return 'boldTitle';
            }

            if (content.includes(':') && content.length < 150) {
                return 'subheading';
            }

            return 'paragraph';
        };

        const renderRiskTable = (content) => {
            const lines = content.split('\n');
            const tableRows = [];
            let headers = [];
            let isHeaderProcessed = false;
            let tableFormat = 'pipe'; // 'pipe' or 'tab' or 'space'

            // First, try to detect table format
            lines.forEach((line) => {
                const trimmedLine = line.trim();

                if (trimmedLine.includes('**Risk Category**') &&
                    trimmedLine.includes('**Risk Description**')) {
                    if (trimmedLine.includes('|')) {
                        tableFormat = 'pipe';
                    } else if (trimmedLine.includes('\t')) {
                        tableFormat = 'tab';
                    } else {
                        tableFormat = 'space';
                    }
                }
            });

            // Parse table lines
            lines.forEach((line) => {
                const trimmedLine = line.trim();

                if (!trimmedLine) return;

                // Skip separator lines
                if (trimmedLine.includes('---') || trimmedLine.includes('===')) {
                    return;
                }

                let cells = [];

                // Parse based on detected format
                if (tableFormat === 'pipe' && trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
                    cells = trimmedLine
                        .split('|')
                        .slice(1, -1)
                        .map(cell => cell.trim());
                }
                else if (tableFormat === 'tab' && trimmedLine.includes('\t')) {
                    cells = trimmedLine.split('\t').map(cell => cell.trim());
                }
                else if (tableFormat === 'space' && trimmedLine.includes('  ')) {
                    // Handle multiple spaces as separator
                    cells = trimmedLine.split(/\s{2,}/).map(cell => cell.trim());
                }
                else if (trimmedLine.includes('**Risk Category**') &&
                    trimmedLine.includes('**Risk Description**')) {
                    // Parse header line with **bold** text
                    const headerLine = trimmedLine;

                    if (tableFormat === 'pipe') {
                        cells = headerLine.split('|').filter(cell => cell.trim()).map(cell => {
                            // Remove ** from bold text
                            return cell.replace(/\*\*/g, '').trim();
                        });
                    } else if (tableFormat === 'tab') {
                        cells = headerLine.split('\t').map(cell => {
                            return cell.replace(/\*\*/g, '').trim();
                        });
                    } else {
                        // For space separated
                        cells = headerLine.split(/\s{2,}/).map(cell => {
                            return cell.replace(/\*\*/g, '').trim();
                        });
                    }

                    if (cells.length > 0 && !isHeaderProcessed) {
                        headers = cells;
                        isHeaderProcessed = true;
                    }
                    return;
                }
                else {
                    // Not a table row
                    return;
                }

                if (cells.length > 0) {
                    if (!isHeaderProcessed) {
                        // Clean up header cells (remove ** markers)
                        headers = cells.map(cell => cell.replace(/\*\*/g, '').trim());
                        isHeaderProcessed = true;
                    } else {
                        // Clean up data cells
                        tableRows.push(cells.map(cell => cell.replace(/\*\*/g, '').trim()));
                    }
                }
            });

            // If no proper table found, render as paragraph
            if (headers.length === 0 || tableRows.length === 0) {
                return renderParagraph(content);
            }

            return (
                <div className="risk-assessment-container mt-3">
                    <div className="table-responsive" style={{
                        border: `1px solid ${colorClass.cardborder}40`,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        marginBottom: '25px'
                    }}>
                        <table className="table table-hover mb-0" style={{
                            fontSize: '14px',
                            margin: 0
                        }}>
                            <thead style={{
                                background: `linear-gradient(135deg, ${colorClass.cardtext}15, ${colorClass.cardborder}20)`,
                                borderBottom: `2px solid ${colorClass.cardborder}`
                            }}>
                                <tr>
                                    {headers.map((header, index) => (
                                        <th key={index} style={{
                                            padding: '14px 16px',
                                            fontWeight: '700',
                                            color: colorClass.cardtext,
                                            fontSize: '15px',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map((row, rowIndex) => (
                                    <tr key={rowIndex} style={{
                                        background: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
                                        transition: 'background 0.2s'
                                    }}>
                                        {row.map((cell, cellIndex) => {
                                            let cellStyle = {
                                                padding: '12px 16px',
                                                verticalAlign: 'top',
                                                borderBottom: rowIndex < tableRows.length - 1 ? `1px solid ${colorClass.cardborder}20` : 'none'
                                            };

                                            // Style based on cell content
                                            if (cellIndex === 0 && cell) { // Risk Category
                                                cellStyle.fontWeight = '600';
                                                cellStyle.background = `${colorClass.cardtext}08`;
                                            }

                                            if (cellIndex === 4 && cell) { // Risk Score
                                                const score = parseInt(cell) || 0;
                                                if (score >= 60) {
                                                    cellStyle.color = '#dc2626';
                                                    cellStyle.fontWeight = '700';
                                                    cellStyle.background = '#fee2e2';
                                                    cellStyle.borderRadius = '4px';
                                                } else if (score >= 40) {
                                                    cellStyle.color = '#d97706';
                                                    cellStyle.fontWeight = '600';
                                                    cellStyle.background = '#fef3c7';
                                                    cellStyle.borderRadius = '4px';
                                                } else if (score > 0) {
                                                    cellStyle.color = '#059669';
                                                    cellStyle.fontWeight = '600';
                                                    cellStyle.background = '#d1fae5';
                                                    cellStyle.borderRadius = '4px';
                                                }
                                            }

                                            // Style Probability and Impact cells
                                            if (cellIndex === 2 || cellIndex === 3) {
                                                if (cell.includes('High')) {
                                                    cellStyle.color = '#dc2626';
                                                    cellStyle.fontWeight = '600';
                                                } else if (cell.includes('Medium')) {
                                                    cellStyle.color = '#d97706';
                                                    cellStyle.fontWeight = '600';
                                                } else if (cell.includes('Low')) {
                                                    cellStyle.color = '#059669';
                                                    cellStyle.fontWeight = '600';
                                                }
                                            }

                                            // Format mitigation strategies
                                            let cellContent = cell;
                                            if (cellIndex === 5 && cell) {
                                                const strategies = cell.split(/(?=\d+\.\d+\.\d+\s+)/);
                                                cellContent = (
                                                    <div>
                                                        {strategies.map((strategy, idx) => {
                                                            if (!strategy.trim()) return null;
                                                            const parts = strategy.split(/\s+/);
                                                            const number = parts[0];
                                                            const text = parts.slice(1).join(' ');

                                                            return (
                                                                <div key={idx} className="d-flex align-items-start mb-1">
                                                                    <span className="me-2 flex-shrink-0" style={{
                                                                        color: colorClass.cardtext,
                                                                        fontWeight: '600',
                                                                        fontSize: '12px'
                                                                    }}>
                                                                        {number}
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: '13px',
                                                                        color: '#4b5563',
                                                                        lineHeight: '1.5'
                                                                    }}>
                                                                        {text}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            }

                                            return (
                                                <td key={cellIndex} style={cellStyle}>
                                                    {typeof cellContent === 'string' ? (
                                                        <span dangerouslySetInnerHTML={{ __html: formatText(cellContent) }} />
                                                    ) : cellContent}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Risk Score Legend */}
                    <div className="mt-4 p-3" style={{
                        background: '#f8fafc',
                        borderRadius: '8px',
                        borderLeft: `4px solid ${colorClass.cardborder}`
                    }}>
                        <div className="d-flex align-items-center mb-2">
                            <strong style={{
                                color: colorClass.cardtext,
                                marginRight: '15px',
                                fontSize: '15px'
                            }}>Risk Score Legend:</strong>
                            <div className="d-flex gap-4">
                                <div className="d-flex align-items-center">
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '3px',
                                        background: '#d1fae5',
                                        marginRight: '6px',
                                        border: '1px solid #059669'
                                    }}></div>
                                    <small style={{ color: '#6b7280', fontSize: '13px' }}>Low (0-39)</small>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '3px',
                                        background: '#fef3c7',
                                        marginRight: '6px',
                                        border: '1px solid #d97706'
                                    }}></div>
                                    <small style={{ color: '#6b7280', fontSize: '13px' }}>Medium (40-59)</small>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div style={{
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '3px',
                                        background: '#fee2e2',
                                        marginRight: '6px',
                                        border: '1px solid #dc2626'
                                    }}></div>
                                    <small style={{ color: '#6b7280', fontSize: '13px' }}>High (60+)</small>
                                </div>
                            </div>
                        </div>
                        <small style={{ color: '#6b7280', fontSize: '13px' }}>
                            <em>Risk Score = Probability × Impact | Probability & Impact: Low (1-3), Medium (4-6), High (7-10)</em>
                        </small>
                    </div>

                    {/* Render any additional content after table */}
                    {content.includes('Recommendations:') && (
                        <div className="mt-5">
                            <h4 className="fw-bold mb-3" style={{
                                color: colorClass.cardtext,
                                fontSize: '18px',
                                borderBottom: `2px solid ${colorClass.cardborder}30`,
                                paddingBottom: '8px'
                            }}>
                                Recommendations
                            </h4>
                            <div className="ms-3">
                                {content.split('\n').map((line, index) => {
                                    const trimmedLine = line.trim();
                                    const numberMatch = trimmedLine.match(/^(\d+\.)\s+(.+)/);

                                    if (numberMatch && trimmedLine.toLowerCase().includes('recommendation')) {
                                        const [, number, text] = numberMatch;
                                        return (
                                            <div key={index} className="d-flex align-items-start mb-3">
                                                <div className="me-3 flex-shrink-0" style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, ${colorClass.cardtext}, ${colorClass.cardborder})`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px'
                                                }}>
                                                    {number.replace('.', '')}
                                                </div>
                                                <div style={{
                                                    lineHeight: '1.6',
                                                    fontSize: '15px',
                                                    color: '#4b5563',
                                                    flex: 1
                                                }}>
                                                    {text}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                }).filter(item => item)}
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        // Helper to check if line needs indentation
        const needsIndentation = (lines, currentIndex) => {
            if (currentIndex === 0) return false;

            // Check previous non-empty line
            for (let i = currentIndex - 1; i >= 0; i--) {
                const prevLine = lines[i].trim();
                if (!prevLine) continue;

                // If previous line ends with colon (title)
                if (prevLine.endsWith(':')) return true;

                // If previous line is a bullet or sub-bullet
                if (prevLine.startsWith('*') || prevLine.startsWith('-') || prevLine.startsWith('+')) return true;

                // If previous line is bold title
                if (prevLine.includes('**') && prevLine.length < 100) return true;

                // If previous line is numbered item
                if (/^\d+\./.test(prevLine)) return true;

                break; // Only check immediate previous non-empty line
            }

            return false;
        };

        // Helper to get indentation level
        const getIndentationLevel = (lines, currentIndex) => {
            if (currentIndex === 0) return 0;

            let level = 0;
            // Count consecutive indentations
            for (let i = currentIndex - 1; i >= 0; i--) {
                const line = lines[i].trim();
                if (!line) continue;

                if (line.startsWith('+')) {
                    level = 2; // Sub-bullet level
                    break;
                } else if (line.startsWith('*') || line.startsWith('-')) {
                    level = 1; // Bullet level
                    break;
                } else if (line.endsWith(':')) {
                    level = 1; // Title level
                    break;
                }
            }

            return level;
        };

        const renderNumberedList = (content) => {
            const lines = content.split('\n');

            return (
                <div className="numbered-list-container" style={{ margin: '16px 0' }}>
                    {lines.map((line, index) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return <div key={index} style={{ height: '12px' }}></div>;

                        // Check for numbered items (1., 2., etc.)
                        const numberedMatch = trimmedLine.match(/^(\d+\.)\s+(.+)/);

                        if (numberedMatch) {
                            const [, number, text] = numberedMatch;
                            const formattedText = formatText(text);

                            // Check for feasibility score
                            const hasFeasibility = formattedText.includes('Feasibility:');
                            const hasPriority = formattedText.includes('Priority:');

                            if (hasFeasibility || hasPriority) {
                                return (
                                    <div key={index} className="numbered-item-with-metrics">
                                        <style>{`
                                .numbered-item-with-metrics {
                                    margin-bottom: 24px;
                                    padding: 22px;
                                    background: white;
                                    border-radius: 8px;
                                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                                    border-left: 4px solid ${colorClass.cardborder};
                                }
                                
                                .number {
                                    display: inline-block;
                                    background: ${colorClass.cardtext};
                                    color: white;
                                    width: 32px;
                                    height: 32px;
                                    border-radius: 50%;
                                    text-align: center;
                                    line-height: 32px;
                                    font-weight: bold;
                                    margin-right: 15px;
                                    font-size: 16px;
                                }
                                
                                .item-title {
                                    display: inline;
                                    font-size: 18px;
                                    color: ${colorClass.cardtext};
                                    margin-right: 15px;
                                    font-weight: 600;
                                }
                                
                                .metrics {
                                    margin-top: 12px;
                                    display: flex;
                                    gap: 24px;
                                    flex-wrap: wrap;
                                }
                                
                                .metric {
                                    font-size: 16px;
                                    color: #4b5563;
                                }
                                
                                .metric-label {
                                    font-weight: 600;
                                    color: #6b7280;
                                    font-size: 16px;
                                }
                                
                                .feasibility-high {
                                    color: #059669;
                                    font-weight: 600;
                                    font-size: 16px;
                                }
                                
                                .feasibility-medium {
                                    color: #d97706;
                                    font-weight: 600;
                                    font-size: 16px;
                                }
                                
                                .feasibility-low {
                                    color: #dc2626;
                                    font-weight: 600;
                                    font-size: 16px;
                                }
                                
                                .priority-high {
                                    color: #dc2626;
                                    font-weight: 600;
                                    font-size: 16px;
                                }
                                
                                .priority-medium {
                                    color: #d97706;
                                    font-weight: 600;
                                    font-size: 16px;
                                }
                                
                                .priority-low {
                                    color: #059669;
                                    font-weight: 600;
                                    font-size: 16px;
                                }
                            `}</style>

                                        <div>
                                            <span className="number">{number.replace('.', '')}</span>
                                            <span className="item-title" dangerouslySetInnerHTML={{
                                                __html: formattedText.split('(')[0]
                                            }} />
                                        </div>

                                        {(hasFeasibility || hasPriority) && (
                                            <div className="metrics">
                                                {hasFeasibility && (
                                                    <div className="metric">
                                                        <span className="metric-label">Feasibility: </span>
                                                        <span className={
                                                            formattedText.includes('Feasibility: 8/10') ||
                                                                formattedText.includes('Feasibility: 9/10') ||
                                                                formattedText.includes('Feasibility: 10/10')
                                                                ? 'feasibility-high'
                                                                : formattedText.includes('Feasibility: 5/10') ||
                                                                    formattedText.includes('Feasibility: 6/10') ||
                                                                    formattedText.includes('Feasibility: 7/10')
                                                                    ? 'feasibility-medium'
                                                                    : 'feasibility-low'
                                                        }>
                                                            {formattedText.match(/Feasibility:\s*([^,)]+)/)?.[1] || ''}
                                                        </span>
                                                    </div>
                                                )}
                                                {hasPriority && (
                                                    <div className="metric">
                                                        <span className="metric-label">Priority: </span>
                                                        <span className={
                                                            formattedText.includes('Priority: High')
                                                                ? 'priority-high'
                                                                : formattedText.includes('Priority: Medium')
                                                                    ? 'priority-medium'
                                                                    : 'priority-low'
                                                        }>
                                                            {formattedText.match(/Priority:\s*([^,)]+)/)?.[1] || ''}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            // Regular numbered item
                            return (
                                <div key={index} className="d-flex align-items-start mb-3">
                                    <span className="me-3 flex-shrink-0 fw-bold" style={{
                                        color: colorClass.cardtext,
                                        fontSize: '18px'
                                    }}>
                                        {number}
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '17px',
                                        color: '#374151'
                                    }} dangerouslySetInnerHTML={{ __html: formattedText }} />
                                </div>
                            );
                        }

                        // Bullet points within numbered items - INDENT WHEN NEEDED
                        if (trimmedLine.startsWith('*') || trimmedLine.startsWith('-')) {
                            const bulletContent = trimmedLine.substring(1).trim();
                            const formattedContent = formatText(bulletContent);

                            const indentLevel = getIndentationLevel(lines, index);
                            const marginLeft = indentLevel === 0 ? '0' : '24px';

                            return (
                                <div key={index} className="d-flex align-items-start mb-3" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: colorClass.cardborder,
                                        fontSize: '20px'
                                    }}>
                                        •
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '16px',
                                        color: '#4b5563'
                                    }} dangerouslySetInnerHTML={{ __html: formattedContent }} />
                                </div>
                            );
                        }

                        // Sub-bullets with + - ALWAYS INDENTED
                        if (trimmedLine.startsWith('+')) {
                            const subBulletContent = trimmedLine.substring(1).trim();
                            const formattedContent = formatText(subBulletContent);

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft: '48px' }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: '#6b7280',
                                        fontSize: '18px'
                                    }}>
                                        ↳
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '15px',
                                        color: '#6b7280'
                                    }} dangerouslySetInnerHTML={{ __html: formattedContent }} />
                                </div>
                            );
                        }

                        // Regular text after title/bullet - INDENT WHEN NEEDED
                        if (needsIndentation(lines, index)) {
                            const indentLevel = getIndentationLevel(lines, index);
                            const marginLeft = indentLevel === 0 ? '24px' :
                                indentLevel === 1 ? '24px' : '48px';
                            const symbol = indentLevel === 0 ? '↳' : '↳';

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: '#6b7280',
                                        fontSize: '18px'
                                    }}>
                                        {symbol}
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '16px',
                                        color: '#6b7280'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                                </div>
                            );
                        }

                        // Regular text
                        if (trimmedLine) {
                            const contentType = detectContentType(trimmedLine);

                            if (contentType === 'boldTitle') {
                                return (
                                    <h3 key={index} className="fw-bold mt-4 mb-3" style={{
                                        color: colorClass.cardtext,
                                        fontSize: '22px'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                                );
                            }

                            if (contentType === 'subheading') {
                                return (
                                    <h4 key={index} className="fw-semibold mt-3 mb-2" style={{
                                        color: colorClass.cardtext,
                                        fontSize: '20px',
                                        borderBottom: `2px solid ${colorClass.cardborder}`,
                                        paddingBottom: '8px'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                                );
                            }

                            return (
                                <p key={index} className="mb-3" style={{
                                    lineHeight: '1.7',
                                    fontSize: '17px',
                                    color: '#4b5563'
                                }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                            );
                        }

                        return <div key={index} style={{ height: '12px' }}></div>;
                    })}
                </div>
            );
        };

        const renderTitleWithPoints = (content) => {
            const lines = content.split('\n');

            return (
                <div style={{ margin: '16px 0' }}>
                    {lines.map((line, index) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return <div key={index} style={{ height: '12px' }}></div>;

                        // Check for title line (ends with colon)
                        if (trimmedLine.endsWith(':') && !trimmedLine.startsWith('*') && !trimmedLine.startsWith('-')) {
                            return (
                                <h4 key={index} className="fw-bold mt-4 mb-2" style={{
                                    color: colorClass.cardtext,
                                    fontSize: '20px',
                                    borderLeft: `4px solid ${colorClass.cardborder}`,
                                    paddingLeft: '12px'
                                }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                            );
                        }

                        // Check for bullet points with data
                        if (trimmedLine.match(/^\*\s+[A-Z]/)) {
                            const bulletMatch = trimmedLine.match(/^\*\s+(.+?):\s+(.+)/);
                            if (bulletMatch) {
                                const [, label, value] = bulletMatch;
                                const indentNeeded = needsIndentation(lines, index);
                                const marginLeft = indentNeeded ? '24px' : '0';

                                return (
                                    <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                        <span className="me-2 flex-shrink-0" style={{
                                            color: colorClass.cardborder,
                                            fontSize: '20px'
                                        }}>
                                            •
                                        </span>
                                        <div style={{ lineHeight: '1.7', fontSize: '17px', color: '#374151' }}>
                                            <strong style={{ fontSize: '17px' }}>{label}:</strong>
                                            <span style={{ color: '#4b5563', fontSize: '17px' }}> {value}</span>
                                        </div>
                                    </div>
                                );
                            }
                        }

                        // Regular bullet points
                        if (trimmedLine.startsWith('*') || trimmedLine.startsWith('-')) {
                            const bulletContent = trimmedLine.substring(1).trim();
                            const indentNeeded = needsIndentation(lines, index);
                            const marginLeft = indentNeeded ? '24px' : '0';

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: colorClass.cardborder,
                                        fontSize: '20px'
                                    }}>
                                        •
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '17px',
                                        color: '#4b5563'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(bulletContent) }} />
                                </div>
                            );
                        }

                        // Sub-bullets with + - ALWAYS INDENTED
                        if (trimmedLine.startsWith('+')) {
                            const subBulletContent = trimmedLine.substring(1).trim();
                            return (
                                <div key={index} className="d-flex align-items-start mb-1" style={{ marginLeft: '48px' }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: '#6b7280',
                                        fontSize: '18px'
                                    }}>
                                        ↳
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '16px',
                                        color: '#6b7280'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(subBulletContent) }} />
                                </div>
                            );
                        }

                        // Regular text after title/bullet - INDENT WHEN NEEDED
                        if (needsIndentation(lines, index)) {
                            const indentLevel = getIndentationLevel(lines, index);
                            const marginLeft = indentLevel === 0 ? '24px' :
                                indentLevel === 1 ? '24px' : '48px';
                            const symbol = indentLevel === 0 ? '↳' : '↳';

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: '#6b7280',
                                        fontSize: '18px'
                                    }}>
                                        {symbol}
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '16px',
                                        color: '#6b7280'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                                </div>
                            );
                        }

                        // Regular paragraph
                        return (
                            <p key={index} className="mb-3" style={{
                                lineHeight: '1.7',
                                fontSize: '17px',
                                color: '#4b5563'
                            }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                        );
                    })}
                </div>
            );
        };

        const renderBulletList = (content) => {
            const lines = content.split('\n');

            return (
                <div style={{ margin: '16px 0' }}>
                    {lines.map((line, index) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return <div key={index} style={{ height: '12px' }}></div>;

                        if (trimmedLine.startsWith('*') || trimmedLine.startsWith('-')) {
                            const bulletContent = trimmedLine.substring(1).trim();

                            // Check if it's a subheading-like bullet (ends with colon and short)
                            const isSubheadingBullet = bulletContent.endsWith(':') &&
                                bulletContent.length < 50 &&
                                bulletContent.split(' ').length <= 4;

                            const indentNeeded = needsIndentation(lines, index);
                            const marginLeft = indentNeeded ? '24px' : '0';

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: colorClass.cardborder,
                                        fontSize: '20px'
                                    }}>
                                        •
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: isSubheadingBullet ? '18px' : '17px',
                                        color: isSubheadingBullet ? colorClass.cardtext : '#374151',
                                        fontWeight: isSubheadingBullet ? 'bold' : 'normal'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(bulletContent) }} />
                                </div>
                            );
                        }

                        // Sub-bullets with + - ALWAYS INDENTED
                        if (trimmedLine.startsWith('+')) {
                            const subBulletContent = trimmedLine.substring(1).trim();
                            return (
                                <div key={index} className="d-flex align-items-start mb-1" style={{ marginLeft: '48px' }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: '#6b7280',
                                        fontSize: '18px'
                                    }}>
                                        ↳
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '16px',
                                        color: '#6b7280'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(subBulletContent) }} />
                                </div>
                            );
                        }

                        // Regular text after bullet - INDENT WHEN NEEDED
                        if (needsIndentation(lines, index)) {
                            const indentLevel = getIndentationLevel(lines, index);
                            const marginLeft = indentLevel === 0 ? '24px' :
                                indentLevel === 1 ? '24px' : '48px';
                            const symbol = indentLevel === 0 ? '↳' : '↳';

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: '#6b7280',
                                        fontSize: '18px'
                                    }}>
                                        {symbol}
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '16px',
                                        color: '#6b7280'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                                </div>
                            );
                        }

                        return (
                            <p key={index} className="mb-3" style={{
                                lineHeight: '1.7',
                                fontSize: '17px',
                                color: '#4b5563'
                            }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                        );
                    })}
                </div>
            );
        };

        const renderParagraph = (content) => {
            const lines = content.split('\n');

            return (
                <div style={{ margin: '16px 0' }}>
                    {lines.map((line, index) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return <div key={index} style={{ height: '12px' }}></div>;

                        const contentType = detectContentType(trimmedLine);

                        if (contentType === 'boldTitle') {
                            return (
                                <h3 key={index} className="fw-bold mt-4 mb-3" style={{
                                    color: colorClass.cardtext,
                                    fontSize: '22px'
                                }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                            );
                        }

                        if (contentType === 'subheading') {
                            return (
                                <h4 key={index} className="fw-bold mt-3 mb-2" style={{
                                    color: colorClass.cardtext,
                                    fontSize: '20px'
                                }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                            );
                        }

                        // Handle bullet-like lines that should be bold
                        if ((trimmedLine.startsWith('•') || trimmedLine.startsWith('*') || trimmedLine.startsWith('-')) &&
                            trimmedLine.includes(':') &&
                            trimmedLine.length < 50) {
                            const text = trimmedLine.replace(/^[•\*\-]\s*/, '');
                            const indentNeeded = needsIndentation(lines, index);
                            const marginLeft = indentNeeded ? '24px' : '0';

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: colorClass.cardborder,
                                        fontSize: '20px'
                                    }}>
                                        •
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '18px',
                                        color: colorClass.cardtext,
                                        fontWeight: 'bold'
                                    }}>
                                        {text}
                                    </span>
                                </div>
                            );
                        }

                        // Regular text after title/bullet - INDENT WHEN NEEDED
                        if (needsIndentation(lines, index)) {
                            const indentLevel = getIndentationLevel(lines, index);
                            const marginLeft = indentLevel === 0 ? '24px' :
                                indentLevel === 1 ? '24px' : '48px';
                            const symbol = indentLevel === 0 ? '↳' : '↳';

                            return (
                                <div key={index} className="d-flex align-items-start mb-2" style={{ marginLeft }}>
                                    <span className="me-2 flex-shrink-0" style={{
                                        color: '#6b7280',
                                        fontSize: '18px'
                                    }}>
                                        {symbol}
                                    </span>
                                    <span style={{
                                        lineHeight: '1.7',
                                        fontSize: '16px',
                                        color: '#6b7280'
                                    }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                                </div>
                            );
                        }

                        return (
                            <p key={index} className="mb-3" style={{
                                lineHeight: '1.7',
                                fontSize: '17px',
                                color: '#4b5563'
                            }} dangerouslySetInnerHTML={{ __html: formatText(trimmedLine) }} />
                        );
                    })}
                </div>
            );
        };

        // Main rendering logic
        const contentType = detectContentType(text);

        switch (contentType) {
            case 'riskTable':
                return renderRiskTable(text);
            case 'numberedList':
                return renderNumberedList(text);
            case 'titleWithPoints':
                return renderTitleWithPoints(text);
            case 'bulletList':
                return renderBulletList(text);
            default:
                return renderParagraph(text);
        }
    };

    if (loading) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
                    <h5 className="text-primary" style={{ fontSize: '20px' }}>Loading Analysis...</h5>
                </div>
            </div>
        );
    }

    if (!currentSection || !analysis || !businessData) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
                <div className="text-center">
                    <h3 className="text-danger mb-3">Analysis Not Found</h3>
                    <p className="mb-3">Please generate analysis first from the main page.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/existing')}>
                        Go Back to Generate Analysis
                    </button>
                </div>
            </div>
        );
    }

    const colorClass = getColorClasses(currentSection.color);
    const content = analysis[currentSection.key] || 'No analysis content available for this section.';
    const formattedContent = renderFormattedContent(content);

    return (
        <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
            <div className="container-fluid py-3 py-md-5 px-2 px-md-4" style={{ maxWidth: 1400 }}>

                {/* Header - Responsive */}
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mb-3 mb-md-5 px-2">
                    {/* Back button */}
                    <button
                        className={`btn d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                        onClick={() => navigate(-1)}
                        style={{
                            borderRadius: '12px',
                            border: `2px solid ${colorClass?.cardtext || '#1e40af'}`,
                            padding: 'clamp(8px, 2vw, 12px) clamp(16px, 3vw, 24px)',
                            fontSize: 'clamp(14px, 2.5vw, 16px)',
                            transition: 'all 0.3s ease',
                            color: colorClass?.cardtext || '#1e40af',
                            backgroundColor: 'transparent',
                            alignSelf: 'flex-start'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        <ArrowLeft size={18} className="me-2" />
                        Back
                    </button>

                    {/* Title - Centered */}
                    <div className="text-center flex-grow-1">
                        <h1 className="fw-bold mb-1" style={{
                            color: colorClass?.cardtext || '#1e40af',
                            fontSize: 'clamp(1.2rem, 4vw, 2rem)',
                            lineHeight: '1.2',
                            wordBreak: 'break-word'
                        }}>
                            {currentSection?.title}
                        </h1>
                        <p className="text-muted m-0" style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.95rem)' }}>
                            Analysis For: <strong style={{ color: colorClass?.text }}>"{businessData?.businessName || 'Your Business'}"</strong>
                        </p>
                    </div>

                    {/* Spacer for balance */}
                    <div className="d-none d-md-block" style={{ width: '100px' }}></div>
                </div>

                {/* Main Content - Responsive Container */}
                <div className="px-0 px-md-3">
                    <div className="mx-auto" style={{
                        maxWidth: '100%',
                        borderRadius: 'clamp(8px, 2vw, 16px)',
                        overflow: 'hidden'
                    }}>
                        {/* Content Card - Mobile: no border, full width */}
                        <div className="p-3 p-md-5" style={{
                            background: '#f8fafc',
                            minHeight: 'clamp(300px, 60vh, 600px)',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <div className="p-3 p-md-4 rounded-3" style={{
                                background: 'white',
                                border: window.innerWidth > 768 ? `1px solid ${colorClass?.cardborder || '#93c5fd'}` : 'none',
                                borderRadius: 'clamp(8px, 2vw, 12px)',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                boxShadow: window.innerWidth > 768 ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
                            }}>
                                {/* Scrollable Content */}
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    paddingRight: '8px',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: `${colorClass?.cardborder || '#93c5fd'} #f0f0f0`
                                }}>
                                    <div style={{
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'inherit',
                                        fontSize: 'clamp(14px, 2vw, 16px)',
                                        lineHeight: '1.7',
                                        color: '#374151',
                                        textAlign: 'left',
                                        padding: '4px'
                                    }}>
                                        {formattedContent}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer - Action Buttons - Responsive */}
                        <div className="p-2 p-md-3 d-flex justify-content-between align-items-center"
                            style={{
                                background: 'white',
                                borderTop: window.innerWidth > 768 ? '1px solid rgba(0,0,0,0.1)' : 'none'
                            }}>
                            {/* Left side - Date and Brand */}
                            <div className="d-flex align-items-center gap-2 gap-md-3">
                                <small className="text-muted d-flex align-items-center" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                                    <Calendar size={window.innerWidth > 576 ? 16 : 14} className="me-1 me-md-2" />
                                    {new Date().toLocaleDateString('en-US', {
                                        month: window.innerWidth > 576 ? 'long' : 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </small>
                                <small className="text-muted d-none d-sm-block" style={{ fontSize: 'clamp(11px, 2vw, 13px)' }}>
                                    InsightDraft AI
                                </small>
                            </div>

                            {/* ✅ Right side - Action Buttons */}
                            <div className="d-flex gap-2 gap-md-3">
                                <button
                                    className="btn d-flex align-items-center justify-content-center px-3 px-md-4 py-2"
                                    style={{
                                        borderRadius: '8px',
                                        background: colorClass?.cardbg || '#dbeafe',
                                        color: colorClass?.cardtext || '#1e40af',
                                        border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`,
                                        fontSize: 'clamp(13px, 2vw, 15px)',
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onClick={() => copyToClipboard(formattedContent?.props?.dangerouslySetInnerHTML?.__html || content)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardtext || '#1e40af';
                                        e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe';
                                        e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                                    }}
                                >
                                    <Copy size={window.innerWidth > 576 ? 18 : 16} className="me-1 me-md-2" />
                                    {copied ? 'Copied!' : (window.innerWidth > 576 ? 'Copy Text' : 'Copy')}
                                </button>

                                <button
                                    className="btn d-flex align-items-center justify-content-center px-3 px-md-4 py-2"
                                    style={{
                                        borderRadius: '8px',
                                        background: colorClass?.cardbg || '#dbeafe',
                                        color: colorClass?.cardtext || '#1e40af',
                                        border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`,
                                        fontSize: 'clamp(13px, 2vw, 15px)',
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onClick={() => downloadAsPDF(currentSection?.title, content)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardtext || '#1e40af';
                                        e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe';
                                        e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                                    }}
                                >
                                    <Download size={window.innerWidth > 576 ? 18 : 16} className="me-1 me-md-2" />
                                    {window.innerWidth > 576 ? 'Download PDF' : 'PDF'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Back to Full Analysis Button */}
                <div className="text-center mt-4 mt-md-5">
                    <button
                        className={`btn px-4 px-md-5 py-2 py-md-3`}
                        style={{
                            borderRadius: '12px',
                            background: colorClass?.cardbg || '#dbeafe',
                            color: colorClass?.cardtext || '#1e40af',
                            border: `1px solid ${colorClass?.cardborder || '#93c5fd'}`,
                            fontSize: 'clamp(14px, 2.5vw, 16px)',
                            transition: 'all 0.3s ease'
                        }}
                        onClick={() => {
                            const savedType = localStorage.getItem('analysisType') || 'existing';
                            navigate(`/analysis?type=${savedType}`);
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = colorClass?.cardbg || '#dbeafe';
                            e.currentTarget.style.color = colorClass?.cardtext || '#1e40af';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        <ArrowLeft size={18} className="me-2" />
                        Back to Full Analysis
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CategoryPage;